import React, { useState, useEffect } from "react";
import { useGetDocuments, useCreateDocument, useUpdateDocument } from "./hooks";
import { useT } from "../../i18n";

interface DocumentData {
  url: string;
}

const DocumentsPage: React.FC = () => {
  const t = useT();
  const [documentData, setDocumentData] = useState<DocumentData>({
    url: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [existingDocumentId, setExistingDocumentId] = useState<string | null>(
    null
  );

  const { data: documents, isLoading } = useGetDocuments();
  const createDocument = useCreateDocument();
  const updateDocument = useUpdateDocument();

  // Load existing document if available
  useEffect(() => {
    if (documents && documents.length > 0) {
      const doc = documents[0]; // Get the first (and only) document
      setDocumentData({
        url: doc.url,
      });
      setExistingDocumentId(doc.id);
      setIsEditing(true);
    }
  }, [documents]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDocumentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && existingDocumentId) {
        await updateDocument.mutateAsync({
          id: existingDocumentId,
          data: {
            url: documentData.url,
          },
        });
      } else {
        await createDocument.mutateAsync({
          url: documentData.url,
        });
        setIsEditing(true);
      }
    } catch (error) {
      console.error("Error saving document:", error);
    }
  };

  const handleReset = () => {
    setDocumentData({
      url: "",
    });
    setIsEditing(false);
    setExistingDocumentId(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
            <p className="text-gray-600">{t("documents.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="flex items-center justify-between sm:h-20 px-4 sm:px-8 py-4 sm:py-0 bg-white border-b border-gray-200">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            {t("documents.title")}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {isEditing
              ? t("documents.subtitleUpdate")
              : t("documents.subtitleAdd")}
          </p>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-8">
        <div className="max-w-2xl">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="url"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("documents.urlLabel")}
                </label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={documentData.url}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t("documents.urlPlaceholder")}
                />
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={
                      createDocument.isPending || updateDocument.isPending
                    }
                    className="px-6 py-2.5 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    {createDocument.isPending || updateDocument.isPending ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        {isEditing
                          ? t("common.loading.updating")
                          : t("common.loading.saving")}
                      </>
                    ) : (
                      <>
                        <i
                          className={`fa-solid ${
                            isEditing ? "fa-save" : "fa-plus"
                          } mr-2`}
                        ></i>
                        {isEditing
                          ? t("documents.updateUrl")
                          : t("documents.saveUrl")}
                      </>
                    )}
                  </button>
                </div>

                {documentData.url && (
                  <a
                    href={documentData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    <i className="fa-solid fa-external-link mr-2"></i>
                    {t("documents.previewDocument")}
                  </a>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentsPage;
